import { Check } from "lucide-react";
import { useClientThemeGuard } from "@/hooks/useClientThemeGuard";

/** Client Portal v2 (2026-09, Phase 1) — a MILESTONE RAIL (Brief Received -> ... -> Completed),
 * each stage marked done/current/upcoming from the project's own Status (kernel-computed, see
 * modules/client_portal.py's get_timeline). Deliberately NOT a percentage bar and NOT a
 * chronological activity feed. Restyled for the warm light content surface — was a dark
 * `cp-card`; now a `cp-panel` (white) so it reads as part of the page, not an overlay.
 *
 * Client-Portal-only: depends on `.cp-theme`-scoped CSS variables (clientPortal.css) — do NOT
 * reuse on a staff page. `useClientThemeGuard` fails loud in dev if this ever happens again. */
export function TimelineRail({ stages }) {
  const guardRef = useClientThemeGuard("TimelineRail");
  if (!stages || stages.length === 0) return null;
  return (
    <div ref={guardRef} className="cp-panel p-5 md:p-7 overflow-x-auto" data-testid="client-portal-timeline-rail">
      <div className="flex items-start min-w-[640px]">
        {stages.map((s, i) => (
          <div key={s.key} className="flex items-center flex-1 last:flex-none" data-testid={`client-portal-timeline-stage-${s.key}`} data-state={s.state}>
            <div className="flex flex-col items-center text-center w-[110px] shrink-0">
              <div
                className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-colors ${
                  s.state === "done"
                    ? "bg-[color:var(--cp-gold)] border-[color:var(--cp-gold)]"
                    : s.state === "current"
                    ? "border-[color:var(--cp-gold)] bg-white shadow-[0_0_0_4px_rgba(255,200,23,.18)]"
                    : "border-black/15 bg-white"
                }`}
              >
                {s.state === "done" ? (
                  <Check className="h-4 w-4 text-[color:var(--cp-ink)]" />
                ) : (
                  <span className={`h-2 w-2 rounded-full ${s.state === "current" ? "bg-[color:var(--cp-gold)]" : "bg-[color:var(--cp-grey-5)]"}`} />
                )}
              </div>
              <span className={`mt-3 text-[11px] uppercase tracking-wide leading-tight ${s.state === "upcoming" ? "text-[color:var(--cp-grey-4)]" : "text-[color:var(--cp-ink-text)]"}`}>{s.label}</span>
              {s.state === "current" && <span className="mt-1 text-[10px] font-semibold text-[color:var(--cp-gold-dim)]">In progress</span>}
            </div>
            {i < stages.length - 1 && <div className={`h-[2px] flex-1 min-w-[16px] ${s.state === "done" ? "bg-[color:var(--cp-gold)]" : "bg-[color:var(--cp-surface-line)]"}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}
